import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getReservations,
  getReservation,
  createReservation,
  editReservation,
  approveReservation,
  finalizeReservation,
  rejectReservation,
  getReservationHistory,
  getAvailableStock,
} from "../../../services";
import type {
  ReservationListParams,
  CreateReservationPayload,
  EditReservationPayload,
  ReservationHistoryParams,
} from "../../../privatePages/Reservations/types";
import { QUERY_KEYS } from "../queryKeys";

/**
 * Fetches paginated reservations for the authenticated supplier.
 * GET /v2/reservations
 */
export const useGetReservations = (params?: ReservationListParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_RESERVATIONS, params ?? null],
    queryFn: () => getReservations(params),
    refetchOnWindowFocus: false,
  });
};

/**
 * Fetches a single reservation detail with items.
 * GET /v2/reservations/:id
 */
export const useGetReservation = (id: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_RESERVATION, id],
    queryFn: () => getReservation(id),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

/**
 * Mutation to create a new pending reservation.
 * POST /v2/reservations
 * Invalidates GET_RESERVATIONS on success.
 */
export const useCreateReservation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateReservationPayload) =>
      createReservation(payload),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_RESERVATIONS]);
    },
  });
};

/**
 * Mutation to edit a pending reservation.
 * PATCH /v2/reservations/:id
 * Invalidates GET_RESERVATIONS on success.
 */
export const useEditReservation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: EditReservationPayload }) =>
      editReservation(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_RESERVATIONS]);
    },
  });
};

/**
 * Mutation to approve a pending reservation (reserves stock without decrementing physical stock).
 * POST /v2/reservations/:id/approve
 * Invalidates GET_RESERVATIONS and GET_INVENTORY on success.
 */
export const useApproveReservation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      approveReservation(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_RESERVATIONS]);
      queryClient.invalidateQueries([QUERY_KEYS.GET_INVENTORY]);
    },
  });
};

/**
 * Mutation to finalize an approved reservation and dispatch physical stock.
 * POST /v2/reservations/:id/finalize
 * Invalidates GET_RESERVATIONS and GET_INVENTORY on success.
 */
export const useFinalizeReservation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: number; notes?: string }) =>
      finalizeReservation(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_RESERVATIONS]);
      queryClient.invalidateQueries([QUERY_KEYS.GET_INVENTORY]);
    },
  });
};

/**
 * Mutation to reject a pending reservation.
 * POST /v2/reservations/:id/reject
 * Invalidates GET_RESERVATIONS on success.
 */
export const useRejectReservation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      rejectReservation(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_RESERVATIONS]);
    },
  });
};

/**
 * Fetches paginated reservation history.
 * GET /v2/reservations/history
 */
export const useGetReservationHistory = (params?: ReservationHistoryParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_RESERVATION_HISTORY, params ?? null],
    queryFn: () => getReservationHistory(params),
    refetchOnWindowFocus: false,
  });
};

/**
 * Fetches the available stock for the currently-selected product+address pairs.
 * Pass the query string built by buildAvailableStockQuery (comma-joined
 * "product_id:storage_address_id" pairs) or null to disable the fetch.
 * GET /v2/reservations/available-stock?items=PID:AID,PID:AID
 *
 * - staleTime 60s: stock changes are real-world events, not worth refetching
 *   on every render or window focus.
 * - enabled only when a query string is present (at least one row fully
 *   selected), so we never fire an empty request.
 */
export const useGetAvailableStock = (items: string | null) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_AVAILABLE_STOCK, items],
    queryFn: () => getAvailableStock(items as string),
    enabled: !!items,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
};
